import RefineRecommendations from "@components/RequestPageComponents/RefineRecommendations";
import RequestPageFinalistCount from "@components/RequestPageComponents/RequestPageFinalistCount";
import StatusCircle from "@components/StatusCircle";
import { Button, Icon } from "@components/library";
import FuelModal from "@components/modals/FuelModal";
import JoinWaitlistModal from "@components/modals/JoinWaitlistModal";
import { COLORS, FEATURE_FLAGS, FONTS, STYLES } from "@constants";
import { useSearchParams } from "@hooks/router";
import useFollowRequest from "@hooks/useFollowRequest";
import { toggleLoginModal } from "@redux/actions/profileActions";
import { RootState } from "@redux/store";
import { getCanSubmitProposal } from "@requests/requests";
import { SegmentEventName } from "@tsTypes/__generated__/enums";
import { RfpPageRequest } from "@tsTypes/index";
import { LaunchableRequestStatus } from "@tsTypes/requests";
import { UserRole } from "@tsTypes/users";
import { track } from "@utils/appUtils";
import { setPrevUrlCookie } from "@utils/authUtils";
import { formatDateToEastern } from "@utils/dateUtils";
import { getLaunchableRequestStatus, requestProposalUrlPath } from "@utils/requestUtils";
import dayjs from "dayjs";
import { ReactElement, ReactNode, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

const { ACTIVE, COMING_SOON, IN_EXTENSION, CLOSED } = LaunchableRequestStatus;

interface Props {
  request: RfpPageRequest;
  asPreview: boolean;
  setIsShareModalOpen: (isOpen: boolean) => void;
}

const RfpCtaContainer = ({ request, asPreview, setIsShareModalOpen }: Props) => {
  const [isFuelModalOpen, setIsFuelModalOpen] = useState(false);
  const [isJoinWaitlistModalOpen, setIsJoinWaitlistModalOpen] = useState(false);

  const rfp = request.requestable;

  const currentUser = useSelector((state: RootState) => state.profiles.currentUser);

  const dispatch = useDispatch();
  const history = useHistory();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("share") === "true") handleShare();
  }, []);

  const handleSubmitClick = async () => {
    track(SegmentEventName.CLICK, {
      ui_component: "Submit Proposal Button",
    });

    const submitUrl = requestProposalUrlPath(request);

    const isFuel =
      "company_name" in request.company && request.company.company_name?.includes("FUEL");

    if (currentUser.id) {
      if (isFuel) {
        const canSubmit = await getCanSubmitProposal(request.id);

        if (canSubmit) {
          history.push(submitUrl);
        } else {
          setIsFuelModalOpen(true);
        }
      } else {
        history.push(submitUrl);
      }
    } else {
      if (isFuel) {
        setPrevUrlCookie();
      } else {
        setPrevUrlCookie(submitUrl);
      }

      dispatch(toggleLoginModal({ open: true }));
    }
  };

  const { handleFollowRequest, isUserFollowing } = useFollowRequest(request.id);

  const handleNotify = () => {
    track("Clicked follow RFP button", {
      isUniversityMember: currentUser.role === UserRole.UNIVERSITY_ADMIN,
    });

    handleFollowRequest();
  };

  const handleShare = () => {
    track("Clicked share RFP button");

    if (currentUser.id) {
      setIsShareModalOpen(true);
    } else {
      dispatch(toggleLoginModal({ open: true }));
    }
  };

  const handleJoinWaitlist = () => {
    track("Clicked join waitlist button");
    setIsJoinWaitlistModalOpen(true);
  };

  const isJamesStanton = currentUser.id === 24;

  const rfpStatus = getLaunchableRequestStatus(request);

  let status: ReactNode = rfp.deadline && (
    <>
      <StatusCircle margin="0 8px 0 0" /> Deadline: {formatDateToEastern(rfp.deadline, "MMMM D")}
    </>
  );
  if (
    FEATURE_FLAGS.ARCHIVE_REQUESTS &&
    request.archived_at &&
    request.finalist_count > 0 &&
    !isJamesStanton
  ) {
    status = <RequestPageFinalistCount count={request.finalist_count} />;
  } else if (rfpStatus === IN_EXTENSION || rfpStatus === CLOSED) {
    status = (
      <>
        <Icon margin="0 4px 0 0" name="Success" size="sm" /> Complete
      </>
    );
  } else if (rfpStatus === COMING_SOON) {
    status = `Coming Soon: ${dayjs(rfp.launch_date ?? 0).format("MMMM D")}`;
  }

  const viewAsScientist = currentUser.role !== 2;

  const canApply =
    rfpStatus === ACTIVE || (rfpStatus === IN_EXTENSION && request.enabled) || isJamesStanton;

  const hasIncompleteProposal = Boolean(
    currentUser.profile_info.proposals?.find(
      (proposal) => proposal.request_id === request.id && !proposal.completed
    )
  );

  let heading = "Share partnering request";
  if (viewAsScientist) {
    if (canApply) {
      heading = "Submit Proposal";
    } else if (rfpStatus === COMING_SOON) {
      heading = "Follow partnering request";
    }
  }

  let scientistPrimaryButton: ReactElement | null = null;
  let universityPrimaryButton = (
    <Button leftIconName="Share" margin="0 0 10px" onClick={handleShare}>
      Share
    </Button>
  );
  if (!request.company.is_partner) {
    scientistPrimaryButton = (
      <Button
        leftIconName="Open in a new tab"
        onClick={() =>
          "url" in request.company &&
          request.company.url &&
          window.open(request.company.url, "_blank")
        }
      >
        View Site
      </Button>
    );
  } else if (canApply) {
    scientistPrimaryButton = (
      <Button
        data-testid="rfp-page-submit-proposal-button"
        leftIconName={hasIncompleteProposal && !isJamesStanton ? "Attention" : "Easy Apply"}
        onClick={handleSubmitClick}
      >
        {hasIncompleteProposal && !isJamesStanton ? "Complete Proposal" : "Easy Apply"}
      </Button>
    );
  } else if (rfpStatus === COMING_SOON) {
    scientistPrimaryButton = isUserFollowing ? (
      <FollowingConfirmation>
        <Icon color={COLORS.GREEN} margin="0 4px 0 0" name="Success" />
        You&apos;ll be notified
      </FollowingConfirmation>
    ) : (
      <Button leftIconName="Bell" margin="10px 0 0" onClick={handleNotify}>
        Notify Me
      </Button>
    );
  } else if (rfpStatus === IN_EXTENSION || rfpStatus === CLOSED) {
    scientistPrimaryButton = (
      <Button leftIconName="Share" onClick={handleShare}>
        Share
      </Button>
    );
  }

  if (rfp.is_trial) {
    if (rfpStatus === ACTIVE) {
      status = (
        <>
          <StatusCircle margin="0 8px 0 0">
            <Icon color={COLORS.WHITE} name="History" size="sm" />
          </StatusCircle>
          Accepting first 5 proposals
        </>
      );
    } else if (rfpStatus === IN_EXTENSION && request.enabled) {
      heading = "Join wait list";
      status = "Get notified if this partnering request reopens.";
      scientistPrimaryButton = (
        <Button leftIconName="History" onClick={handleJoinWaitlist}>
          Join waitlist
        </Button>
      );
      universityPrimaryButton = scientistPrimaryButton;
    }
  }

  useEffect(() => {
    if (rfp.is_trial && rfpStatus === IN_EXTENSION && request.enabled) {
      setIsJoinWaitlistModalOpen(true);
    }
  }, [rfp.is_trial, rfpStatus]);

  return (
    <>
      <FuelModal isOpen={isFuelModalOpen} onClose={() => setIsFuelModalOpen(false)} />
      <JoinWaitlistModal
        isOpen={isJoinWaitlistModalOpen}
        onClose={() => setIsJoinWaitlistModalOpen(false)}
        handleShare={handleShare}
        request={request}
      />
      <Container isSponsor={currentUser.role === 1}>
        <TopContainer $showBorder={FEATURE_FLAGS.RELEVANT_NOT_RELEVANT_BUTTONS}>
          <Heading>{heading}</Heading>
          {!asPreview && <Status>{status}</Status>}
          {viewAsScientist ? scientistPrimaryButton : universityPrimaryButton}
          {Boolean(viewAsScientist && (rfpStatus === COMING_SOON || canApply)) && (
            <Button
              leftIconName="Share"
              margin={`10px 0 ${rfpStatus === COMING_SOON || rfpStatus === ACTIVE ? "10px" : 0}`}
              onClick={handleShare}
              variant="secondary"
            >
              Share
            </Button>
          )}
        </TopContainer>
        {Boolean(FEATURE_FLAGS.RELEVANT_NOT_RELEVANT_BUTTONS && !asPreview) && (
          <RefineRecommendations
            request={request}
            isComplete={rfpStatus === IN_EXTENSION || rfpStatus === CLOSED}
          />
        )}
      </Container>
    </>
  );
};

export default RfpCtaContainer;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  pointer-events: ${({ isSponsor }) => (isSponsor ? "none" : "auto")};

  border-radius: 8px;
  border: 1px solid ${COLORS.NEUTRAL_250};
  box-shadow: ${STYLES.SHADOW_B};
`;
const TopContainer = styled.div<{ $showBorder?: boolean }>`
  display: flex;
  flex-direction: column;
  text-align: center;

  width: 100%;
  padding: 24px 24px 32px;

  ${({ $showBorder }) => $showBorder && `border-bottom: 1px solid ${COLORS.NEUTRAL_250};`}
`;
const Heading = styled.div`
  ${FONTS.SEMIBOLD_1};
  margin-bottom: 8px;
`;
const Status = styled.div`
  display: flex;
  align-items: center;
  margin: 0 auto 16px;
  height: 24px;
  ${FONTS.REGULAR_2};
  color: ${COLORS.NEUTRAL_500};
  pointer-events: auto;
`;
const FollowingConfirmation = styled.div`
  display: flex;
  align-items: center;
  height: 42px;
  color: ${COLORS.GREEN};
  ${FONTS.MEDIUM_2};
  margin: 0 auto 10px;
`;
